//External modules
import React from 'react';
import { useStoreState } from '@/store';

// Internal modules
import {
  getNationalityBasedKycValue,
  hasTreatment,
} from '@/lib/features/utils';
import { features } from '@/lib';
import type { DateOfBirthProps } from './DateOfBirth';
import { DateOfBirth } from './DateOfBirth';
import type { GenderProps } from './Gender';
import { Gender } from './Gender';
import type { IdentityDocumentNumberProps } from './IdentityDocumentNumber';
import { IdentityDocumentNumber } from './IdentityDocumentNumber';
import type { IdentityDocumentTypeProps } from './IdentityDocumentType';
import { IdentityDocumentType } from './IdentityDocumentType';
import type { NationalityProps } from './Nationality';
import { Nationality } from './Nationality';
import type { OccupationProps } from './Occupation';
import { Occupation } from './Occupation';
import type { PlaceOfBirthProps } from './PlaceOfBirth';
import { PlaceOfBirth } from './PlaceOfBirth';
import type { SecondaryIdentityDocumentNumberProps } from './SecondaryIdentityDocumentNumber';
import { SecondaryIdentityDocumentNumber } from './SecondaryIdentityDocumentNumber';
import type { SecondaryIdentityDocumentTypeProps } from './SecondaryIdentityDocumentType';
import { SecondaryIdentityDocumentType } from './SecondaryIdentityDocumentType';
import type { CountryOfResidenceProps } from './CountryOfResidence';
import { CountryOfResidence } from './CountryOfResidence';

const nameToComponent = {
  CountryOfResidence: CountryOfResidence,
  DateOfBirth: DateOfBirth,
  Gender: Gender,
  IdentityDocumentNumber: IdentityDocumentNumber,
  IdentityDocumentType: IdentityDocumentType,
  Nationality: Nationality,
  Occupation: Occupation,
  PlaceOfBirth: PlaceOfBirth,
  SecondaryIdentityDocumentNumber: SecondaryIdentityDocumentNumber,
  SecondaryIdentityDocumentType: SecondaryIdentityDocumentType,
};

type KYCFieldsProps =
  | CountryOfResidenceProps
  | DateOfBirthProps
  | GenderProps
  | IdentityDocumentNumberProps
  | IdentityDocumentTypeProps
  | NationalityProps
  | OccupationProps
  | PlaceOfBirthProps
  | SecondaryIdentityDocumentNumberProps
  | SecondaryIdentityDocumentTypeProps;

const KYCFields = (props: KYCFieldsProps): JSX.Element[] => {
  const fiType = useStoreState(({ ui }) => ui.fiType);
  const { value: countryOfResidence } =
    useStoreState(({ form }) => form.countryOfResidence) || {};
  const cardFieldsToDisplay = useStoreState(({ ui }) => ui.cardFieldsToDisplay);
  const shouldReorderPosition = hasTreatment('FEATURE_NATIONALITY_COLLAPSE');
  const nationality = useStoreState(({ form }) => form.nationality) || {};

  const kycIdTypes = getNationalityBasedKycValue(
    features.config.kycIdTypes,
    // @ts-expect-error don't use default object
    nationality.value,
  );

  // Exclude any KYC fields that are displayed earlier
  // in the page before signup fields
  const kycFields = features.config.kycFields.filter((field: string) => {
    switch (field) {
      case 'DateOfBirth':
        return (
          fiType === 'bnpl' || (fiType !== 'bank' && !cardFieldsToDisplay.dob)
        );
      case 'IdentityDocumentType':
        return kycIdTypes.length > 1;
      case 'SecondaryIdentityDocumentNumber':
        return countryOfResidence === 'RU';
      case 'SecondaryIdentityDocumentType':
        return countryOfResidence === 'RU';
      default:
        return true;
    }
  });

  const kycFieldsSet = new Set(kycFields);
  /*
    If FEATURE_NATIONALITY_COLLAPSE is in place, we should re order
    nationality field and place it at the bottom of the form. Right
    before T&C block.
  */
  if (shouldReorderPosition && kycFieldsSet.has('Nationality')) {
    kycFieldsSet.delete('Nationality');
    kycFieldsSet.add('Nationality');
  }

  return Array.from(kycFieldsSet)?.map(fieldName => {
    // @ts-expect-error TODO type fieldname
    const Field = nameToComponent[fieldName];
    return <Field {...props} key={fieldName} />;
  });
};

export { KYCFields };
